import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Sprocket — a small, cute salvage robot in the spirit of a boxy waste-compactor
// bot: a cube body riding two tank treads, a pair of binocular "periscope" eyes
// on a short neck, and two jointed claw arms. It is built to SHOW OFF ANIMATION:
// the expressive parts are named for the actor rig (`body`, `head`, `arm-l`,
// `arm-r`) so each can be driven independently, and the model ships six
// animations (idle bob, curious look-around, a friendly wave, a happy nod, a
// full-body dance and a one-shot power-down) over the same geometry.
//
// Frame: faces +Z (its forward, where the eyes look), sits on the floor (y = 0).
// Authored in world units, a handful of flat-coloured primitives, no textures.
//
// Rig layout (flat, no hierarchy — each named node moves about its own pivot):
//   - "body"  : the torso + neck stalk (everything above the treads). Bobs,
//               rocks and sinks. Pivot at its base where it meets the treads.
//   - "head"  : the eyes assembly on top of the neck. Pans, nods and tilts.
//               Pivot at the neck joint so it swivels on the neck, not the floor.
//   - "arm-l" / "arm-r" : the side arms, hung at the shoulders. Pivot at each
//               shoulder so they raise and swing like real arms.
// The treads are static (unnamed) and merged into the base mesh — the robot
// rolls in spirit, not in geometry.

const BODY = "#e0a32e";       // warm amber shell (weathered-yellow salvage bot)
const BODY_DARK = "#bd861d";  // shaded panels / recesses
const HATCH = "#7a5a16";      // the front compactor slot, sunk and dark
const TREAD = "#33343a";      // rubber tank tread
const HUB = "#5a5c64";        // tread wheels / hubs
const METAL = "#9aa0a6";      // neck, shoulders, arm joints
const ARM = "#6f737a";        // gunmetal arm segments (distinct from the amber shell)
const EYE = "#2c2f36";        // dark eye housings + brow bar
const LENS = "#79d6ea";       // glowing cyan lenses — the cute, expressive eyes
const ACCENT = "#e8542b";     // warning-orange antenna tip / detail

// ── frame heights ────────────────────────────────────────────────────────────
const TREAD_H = 0.18;         // treads span y = 0 .. 0.18
const TREAD_TOP = TREAD_H;    // body sits on this
const BODY_H = 0.30;
const BODY_CY = TREAD_TOP + BODY_H / 2;     // body centre
const BODY_TOP = TREAD_TOP + BODY_H;        // 0.48
const NECK_TOP = BODY_TOP + 0.08;           // 0.56 — the head swivels here, at its base
const EYE_CY = NECK_TOP + 0.095;            // 0.655 — eyes sit well above the pivot
const SHOULDER_Y = BODY_TOP - 0.05;         // arm hinge height (high on the body side)
const SHOULDER_X = 0.205;                    // arm hinge offset (just outboard of the shell)
const SHOULDER_Z = 0.05;                      // arms set slightly forward so they read head-on

// The pivots the animations rotate the named nodes about.
export const BODY_PIVOT: [number, number, number] = [0, TREAD_TOP, 0];
export const HEAD_PIVOT: [number, number, number] = [0, NECK_TOP, 0];
export const SHOULDER_L: [number, number, number] = [-SHOULDER_X, SHOULDER_Y, SHOULDER_Z];
export const SHOULDER_R: [number, number, number] = [SHOULDER_X, SHOULDER_Y, SHOULDER_Z];

// One tank tread: a rounded-cornered block (a box plus a wheel at each end) with
// three hubs along its side, mirrored to the left/right of the body.
function tread(x: number): Part[] {
  const len = 0.40, h = TREAD_H, w = 0.12, cy = h / 2, r = h / 2;
  const wheel = (z: number): Part => ({
    geometry: new THREE.CylinderGeometry(r, r, w + 0.005, 14),
    color: TREAD, position: [x, cy, z], rotation: [0, 0, Math.PI / 2],
  });
  const hub = (z: number): Part => ({
    geometry: new THREE.CylinderGeometry(0.035, 0.035, w + 0.02, 10),
    color: HUB, position: [x, cy, z], rotation: [0, 0, Math.PI / 2],
  });
  return [
    // The tread belt: a centre box flanked by a rounded wheel at each end.
    { geometry: new THREE.BoxGeometry(w, h, len), color: TREAD, position: [x, cy, 0] },
    wheel(len / 2), wheel(-len / 2),
    // Side hubs (front, middle, back) — the rolling detail.
    hub(0.13), hub(0), hub(-0.13),
  ];
}

// A dark belly skirt filling the whole volume between the two treads, plus axle
// stubs reaching into each track. It closes the daylight that otherwise shows
// between the tracks under the body (from the front and back), so the shell
// reads as solidly MOUNTED on the chassis rather than floating. Static.
function chassis(): Part[] {
  return [
    // Belly plate: spans tread-inner to tread-inner (x +/-0.13, slight overlap so
    // no seam) and the full tread length (z +/-0.20), from the floor clearance up
    // to the body underside.
    { geometry: new THREE.BoxGeometry(0.26, 0.14, 0.40), color: HUB, position: [0, 0.10, 0] },
    // A darker under-rail for shading.
    { geometry: new THREE.BoxGeometry(0.27, 0.04, 0.34), color: TREAD, position: [0, 0.05, 0] },
    // Four axle stubs reaching out into the treads.
    { geometry: new THREE.CylinderGeometry(0.028, 0.028, 0.10, 8), color: METAL, position: [-0.15, 0.11, 0.13], rotation: [0, 0, Math.PI / 2] },
    { geometry: new THREE.CylinderGeometry(0.028, 0.028, 0.10, 8), color: METAL, position: [0.15, 0.11, 0.13], rotation: [0, 0, Math.PI / 2] },
    { geometry: new THREE.CylinderGeometry(0.028, 0.028, 0.10, 8), color: METAL, position: [-0.15, 0.11, -0.13], rotation: [0, 0, Math.PI / 2] },
    { geometry: new THREE.CylinderGeometry(0.028, 0.028, 0.10, 8), color: METAL, position: [0.15, 0.11, -0.13], rotation: [0, 0, Math.PI / 2] },
  ];
}

// The body shell: a tapered cube with a sunk front compactor hatch, a couple of
// shaded side panels and a stubby neck stalk. Named "body" so it bobs and rocks.
function bodyParts(): Part[] {
  return [
    // Main shell, very slightly tapered (wider at the base) for a planted look.
    { geometry: new THREE.BoxGeometry(0.36, BODY_H, 0.32), color: BODY, position: [0, BODY_CY, 0], name: "body" },
    // Front compactor hatch — a dark sunk rectangle so the face-of-the-body reads.
    { geometry: new THREE.BoxGeometry(0.26, 0.18, 0.02), color: HATCH, position: [0, BODY_CY - 0.01, 0.161], name: "body" },
    // Two little hatch latches.
    { geometry: new THREE.BoxGeometry(0.03, 0.03, 0.02), color: METAL, position: [-0.07, BODY_CY - 0.01, 0.172], name: "body" },
    { geometry: new THREE.BoxGeometry(0.03, 0.03, 0.02), color: METAL, position: [0.07, BODY_CY - 0.01, 0.172], name: "body" },
    // A warning stripe along the top front edge.
    { geometry: new THREE.BoxGeometry(0.36, 0.03, 0.02), color: ACCENT, position: [0, BODY_TOP - 0.03, 0.161], name: "body" },
    // Side shaded panels.
    { geometry: new THREE.BoxGeometry(0.02, 0.16, 0.20), color: BODY_DARK, position: [-0.181, BODY_CY, 0], name: "body" },
    { geometry: new THREE.BoxGeometry(0.02, 0.16, 0.20), color: BODY_DARK, position: [0.181, BODY_CY, 0], name: "body" },
    // Back of the body: a recessed vent panel with three louvres + a solar/battery
    // box, so the rear is not a dead face.
    { geometry: new THREE.BoxGeometry(0.26, 0.20, 0.02), color: BODY_DARK, position: [0, BODY_CY - 0.01, -0.161], name: "body" },
    { geometry: new THREE.BoxGeometry(0.22, 0.018, 0.015), color: HUB, position: [0, BODY_CY + 0.05, -0.168], name: "body" },
    { geometry: new THREE.BoxGeometry(0.22, 0.018, 0.015), color: HUB, position: [0, BODY_CY, -0.168], name: "body" },
    { geometry: new THREE.BoxGeometry(0.22, 0.018, 0.015), color: HUB, position: [0, BODY_CY - 0.05, -0.168], name: "body" },
    { geometry: new THREE.BoxGeometry(0.14, 0.06, 0.04), color: METAL, position: [0, BODY_TOP - 0.05, -0.175], name: "body" },
    // Neck post rising from the top, set slightly back so the eyes lead forward;
    // reaches up to NECK_TOP so the head seats right on it (no floating gap).
    { geometry: new THREE.CylinderGeometry(0.05, 0.07, 0.10, 8), color: METAL, position: [0, BODY_TOP + 0.04, -0.02], name: "body" },
  ];
}

// The head: a chunky dark housing that seats on the neck (its base overlaps the
// neck post so no gap opens when it turns), carrying two binocular eye tubes
// (lenses pointing +Z) and a central whip antenna with a warning-orange ball.
// Authored so its mass sits ABOVE the pivot (NECK_TOP) and its base straddles it,
// so panning/nodding reads as a head on a neck, not a detached block. All "head".
function headParts(): Part[] {
  const EYE_X = 0.085, eyeZ = 0.03;
  const eye = (x: number): Part[] => [
    // Eye tube — a forward-pointing cylinder (axis rotated onto Z).
    { geometry: new THREE.CylinderGeometry(0.07, 0.07, 0.13, 14), color: EYE,
      position: [x, EYE_CY, eyeZ], rotation: [Math.PI / 2, 0, 0], name: "head" },
    // Glowing lens at the front face of the tube.
    { geometry: new THREE.CylinderGeometry(0.058, 0.058, 0.02, 14), color: LENS,
      position: [x, EYE_CY, eyeZ + 0.075], rotation: [Math.PI / 2, 0, 0], name: "head" },
    // A tiny dark pupil dot for life.
    { geometry: new THREE.CylinderGeometry(0.022, 0.022, 0.01, 8), color: EYE,
      position: [x, EYE_CY + 0.012, eyeZ + 0.087], rotation: [Math.PI / 2, 0, 0], name: "head" },
  ];
  return [
    // Head housing / "skull": the main dark mass, base straddling the neck top.
    { geometry: new THREE.BoxGeometry(0.24, 0.13, 0.18), color: EYE, position: [0, NECK_TOP + 0.05, -0.01], name: "head" },
    // A collar at the very base, overlapping the neck post to hide the joint.
    { geometry: new THREE.CylinderGeometry(0.06, 0.07, 0.05, 8), color: HUB, position: [0, NECK_TOP, -0.01], name: "head" },
    ...eye(-EYE_X), ...eye(EYE_X),
    // Antenna + ball tip, set back on the head crown and nudged off-centre so it
    // does not line up between the eyes (where it read as a nose/third eye).
    { geometry: new THREE.CylinderGeometry(0.008, 0.008, 0.12, 6), color: METAL, position: [0.05, NECK_TOP + 0.17, -0.075], name: "head" },
    { geometry: new THREE.IcosahedronGeometry(0.024, 0), color: ACCENT, position: [0.05, NECK_TOP + 0.245, -0.075], name: "head" },
  ];
}

// One arm: a shoulder ball, an upper-arm segment and a simple two-finger claw,
// authored hanging at the side. `name` makes it posable about the shoulder.
function arm(name: string, x: number): Part[] {
  const sx = Math.sign(x) * SHOULDER_X;
  const sz = SHOULDER_Z;
  const wristY = SHOULDER_Y - 0.20;       // wrist/palm height
  const prongY = wristY - 0.055;          // prong centres
  return [
    // Shoulder joint.
    { geometry: new THREE.IcosahedronGeometry(0.05, 0), color: METAL, position: [sx, SHOULDER_Y, sz], name },
    // Upper arm hanging straight down (gunmetal, so it reads against the shell).
    { geometry: new THREE.BoxGeometry(0.055, 0.19, 0.055), color: ARM, position: [sx, SHOULDER_Y - 0.105, sz], name },
    // Palm / hand block — a clear, chunky hand so the arm reads as an arm.
    { geometry: new THREE.BoxGeometry(0.075, 0.05, 0.075), color: METAL, position: [sx, wristY, sz], name },
    // Two pincer prongs splayed into a C-claw (front + back), clearly visible.
    { geometry: new THREE.BoxGeometry(0.03, 0.075, 0.028), color: HUB, position: [sx, prongY, sz + 0.028], rotation: [0.35, 0, 0], name },
    { geometry: new THREE.BoxGeometry(0.03, 0.075, 0.028), color: HUB, position: [sx, prongY, sz - 0.028], rotation: [-0.35, 0, 0], name },
  ];
}

export const sprocket = defineModel({
  id: "sprocket",
  batchId: "justtest",
  name: "Sprocket",
  tags: ["robot", "character", "cute", "bot", "animated", "mascot", "scifi"],
  summary: "A cute boxy salvage robot on tank treads, with binocular eyes and claw arms.",
  description:
    "Sprocket is a small, friendly salvage robot: a tapered cube body on two tank " +
    "treads, a pair of binocular periscope eyes on a short neck, and two jointed " +
    "claw arms. Built to demonstrate animation — its body, head and arms are " +
    "separately posable (named for the actor rig) and it carries six animations: " +
    "an idle bob, a curious look-around, a friendly wave, a happy nod, a full-body " +
    "dance and a one-shot power-down.",
  author: "models",
  footprint: 0.35,
  tiles: { w: 1, h: 1 },
  parts(): Part[] {
    return [
      ...tread(-0.18),
      ...tread(0.18),
      ...chassis(),
      ...bodyParts(),
      ...headParts(),
      ...arm("arm-l", -1),
      ...arm("arm-r", 1),
    ];
  },
  animations: [
    // 1. Idle — a slow, breathing bob: the body lifts a touch while the head
    //    dips in counter-phase, the arms sway gently. Reads as "alive, waiting".
    {
      id: "idle",
      label: "Idle bob",
      duration: 3.2,
      loop: true,
      tracks: [
        {
          target: "body", pivot: BODY_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.6, value: [0, 0.018, 0], ease: "sine" },
            { t: 3.2, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "head", pivot: HEAD_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.6, value: [0, 0.024, 0], ease: "sine" },
            { t: 3.2, value: [0, 0, 0], ease: "sine" },
          ],
          rotation: [
            { t: 0, value: [0.04, 0, 0], ease: "sine" },
            { t: 1.6, value: [-0.04, 0, 0], ease: "sine" },
            { t: 3.2, value: [0.04, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "arm-l", pivot: SHOULDER_L,
          rotation: [
            { t: 0, value: [0, 0, 0.05], ease: "sine" },
            { t: 1.6, value: [0, 0, 0.12], ease: "sine" },
            { t: 3.2, value: [0, 0, 0.05], ease: "sine" },
          ],
        },
        {
          target: "arm-r", pivot: SHOULDER_R,
          rotation: [
            { t: 0, value: [0, 0, -0.05], ease: "sine" },
            { t: 1.6, value: [0, 0, -0.12], ease: "sine" },
            { t: 3.2, value: [0, 0, -0.05], ease: "sine" },
          ],
        },
      ],
    },

    // 2. Look-around — the head pans right, then left, then re-centres, with a
    //    little nosey tilt at each end. Pure curiosity, body still.
    {
      id: "look-around",
      label: "Look around",
      duration: 4.0,
      loop: true,
      tracks: [
        {
          target: "head", pivot: HEAD_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 1.0, value: [0, -0.5, -0.07], ease: "easeInOut" },
            { t: 2.0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 3.0, value: [0, 0.5, 0.07], ease: "easeInOut" },
            { t: 4.0, value: [0, 0, 0], ease: "easeInOut" },
          ],
        },
      ],
    },

    // 3. Wave — the right arm raises out to the side and waves twice, the head
    //    cocks toward it. A clear, friendly greeting.
    {
      id: "wave",
      label: "Wave hello",
      duration: 2.4,
      loop: true,
      tracks: [
        // The right arm raises up and out to its own side (positive z about the
        // shoulder), then the hand tips back and forth — a clear, friendly wave.
        {
          target: "arm-r", pivot: SHOULDER_R,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeOut" },
            { t: 0.5, value: [0, 0, 2.0], ease: "easeOut" },
            { t: 0.9, value: [0, 0, 2.3], ease: "easeInOut" },
            { t: 1.3, value: [0, 0, 1.7], ease: "easeInOut" },
            { t: 1.7, value: [0, 0, 2.3], ease: "easeInOut" },
            { t: 2.1, value: [0, 0, 2.0], ease: "easeInOut" },
            { t: 2.4, value: [0, 0, 0], ease: "easeIn" },
          ],
        },
        // The head cocks toward the raised arm.
        {
          target: "head", pivot: HEAD_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeOut" },
            { t: 0.6, value: [0, -0.2, -0.18], ease: "easeOut" },
            { t: 2.0, value: [0, -0.2, -0.18], ease: "easeInOut" },
            { t: 2.4, value: [0, 0, 0], ease: "easeIn" },
          ],
        },
      ],
    },

    // 4. Nod — an eager, happy "yes": the head dips down and up twice while the
    //    whole body gives a small bounce in sympathy.
    {
      id: "nod",
      label: "Happy nod",
      duration: 1.8,
      loop: true,
      tracks: [
        {
          target: "head", pivot: HEAD_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 0.35, value: [0.42, 0, 0], ease: "easeInOut" },
            { t: 0.7, value: [0, 0, 0], ease: "easeInOut" },
            { t: 1.05, value: [0.42, 0, 0], ease: "easeInOut" },
            { t: 1.4, value: [0, 0, 0], ease: "easeInOut" },
            { t: 1.8, value: [0, 0, 0], ease: "easeInOut" },
          ],
        },
        {
          target: "body", pivot: BODY_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.35, value: [0, -0.012, 0], ease: "sine" },
            { t: 0.7, value: [0, 0, 0], ease: "sine" },
            { t: 1.05, value: [0, -0.012, 0], ease: "sine" },
            { t: 1.4, value: [0, 0, 0], ease: "sine" },
            { t: 1.8, value: [0, 0, 0], ease: "sine" },
          ],
        },
      ],
    },

    // 5. Dance — a full-body wiggle: the body rocks side to side about its base,
    //    the head bobs along, and the arms swing in opposition. The showpiece.
    {
      id: "dance",
      label: "Wiggle dance",
      duration: 2.0,
      loop: true,
      tracks: [
        {
          target: "body", pivot: BODY_PIVOT,
          // A clean side-to-side roll about the base: lean right, centre, lean
          // left, centre — a wiggle, not a tip-over (kept modest so a tread does
          // not appear to lift off the ground).
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 0.5, value: [0, 0.1, 0.09], ease: "easeInOut" },
            { t: 1.0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 1.5, value: [0, -0.1, -0.09], ease: "easeInOut" },
            { t: 2.0, value: [0, 0, 0], ease: "easeInOut" },
          ],
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.25, value: [0, 0.018, 0], ease: "sine" },
            { t: 0.5, value: [0, 0, 0], ease: "sine" },
            { t: 0.75, value: [0, 0.018, 0], ease: "sine" },
            { t: 1.0, value: [0, 0, 0], ease: "sine" },
            { t: 1.25, value: [0, 0.018, 0], ease: "sine" },
            { t: 1.5, value: [0, 0, 0], ease: "sine" },
            { t: 1.75, value: [0, 0.018, 0], ease: "sine" },
            { t: 2.0, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "head", pivot: HEAD_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, -0.12], ease: "easeInOut" },
            { t: 0.5, value: [0.1, 0.2, 0.12], ease: "easeInOut" },
            { t: 1.0, value: [0, 0, -0.12], ease: "easeInOut" },
            { t: 1.5, value: [0.1, -0.2, 0.12], ease: "easeInOut" },
            { t: 2.0, value: [0, 0, -0.12], ease: "easeInOut" },
          ],
        },
        {
          target: "arm-l", pivot: SHOULDER_L,
          rotation: [
            { t: 0, value: [0, 0, -1.2], ease: "easeInOut" },
            { t: 0.5, value: [0, 0, 0.3], ease: "easeInOut" },
            { t: 1.0, value: [0, 0, -1.2], ease: "easeInOut" },
            { t: 1.5, value: [0, 0, 0.3], ease: "easeInOut" },
            { t: 2.0, value: [0, 0, -1.2], ease: "easeInOut" },
          ],
        },
        {
          target: "arm-r", pivot: SHOULDER_R,
          rotation: [
            { t: 0, value: [0, 0, 0.3], ease: "easeInOut" },
            { t: 0.5, value: [0, 0, 1.2], ease: "easeInOut" },
            { t: 1.0, value: [0, 0, 0.3], ease: "easeInOut" },
            { t: 1.5, value: [0, 0, 1.2], ease: "easeInOut" },
            { t: 2.0, value: [0, 0, 0.3], ease: "easeInOut" },
          ],
        },
      ],
    },

    // 6. Power-down — a one-shot: the arms drop, the body sinks onto its treads
    //    and the head droops forward as Sprocket goes to sleep. loop: false.
    {
      id: "power-down",
      label: "Power down",
      duration: 1.6,
      loop: false,
      tracks: [
        {
          target: "head", pivot: HEAD_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeIn" },
            { t: 1.2, value: [0.55, 0, 0.12], ease: "easeIn" },
            { t: 1.6, value: [0.55, 0, 0.12] },
          ],
          position: [
            { t: 0, value: [0, 0, 0], ease: "easeIn" },
            { t: 1.2, value: [0, -0.03, 0], ease: "easeIn" },
            { t: 1.6, value: [0, -0.03, 0] },
          ],
        },
        {
          target: "body", pivot: BODY_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "easeIn" },
            { t: 1.2, value: [0, -0.04, 0], ease: "easeIn" },
            { t: 1.6, value: [0, -0.04, 0] },
          ],
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeIn" },
            { t: 1.2, value: [0.08, 0, 0], ease: "easeIn" },
            { t: 1.6, value: [0.08, 0, 0] },
          ],
        },
        {
          target: "arm-l", pivot: SHOULDER_L,
          rotation: [
            { t: 0, value: [0, 0, 0.05], ease: "easeIn" },
            { t: 0.9, value: [0, 0, 0.02], ease: "easeIn" },
            { t: 1.6, value: [0, 0, 0.02] },
          ],
        },
        {
          target: "arm-r", pivot: SHOULDER_R,
          rotation: [
            { t: 0, value: [0, 0, -0.05], ease: "easeIn" },
            { t: 0.9, value: [0, 0, -0.02], ease: "easeIn" },
            { t: 1.6, value: [0, 0, -0.02] },
          ],
        },
      ],
    },
  ],
});
